import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useWizard } from 'react-use-wizard';
import { Check } from 'lucide-react';

import { selectClient, selectSession, selectFeatureFlags } from '@client/model';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { possessive, SessionType } from '@shared';
import { useCareHasStarted } from '@/shared/hooks/use-care-has-started';
import { useActivePilotProgram } from '@/shared/hooks/use-active-pilot-program';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardContent,
  Separator,
} from '@design-system';
import { cn } from '@client/lib';
import { formatCurrency } from '@shared';
import { LtcCostDisplay } from '@client/components/LtcCostDisplay';
import { AM_BEST_CARRIER_RATINGS } from '@client/util/amBestRatingData';
import { getCarrierLogoSrc } from '@client/util/carrierLogo';

import { getStepIndex } from '@/features/guided-financial-planning/config';
import {
  useSession,
  useSessionMutations,
  useGuidedPlanningFlatStepConfigs,
} from '@/features/guided-financial-planning/context';
import {
  useFinalizeSession,
  useFinalizeValidation,
  useAddPolicyFromQuote,
  usePlanComparisonScenarios,
  useSkipPolicySteps,
} from '@/features/guided-financial-planning/hooks';
import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';
import { LicensedAgentCta } from '@/features/guided-financial-planning/components/licensed-agent-cta';
import {
  CarrierBadge,
  type CarrierInfo,
} from '@/features/guided-financial-planning/components/carrier-badge';

type PlanSelection = 'with-insurance' | 'without-insurance';

interface MetricRow {
  label: string;
  value: number;
  variant?: 'default' | 'warning';
  description?: string;
}

interface ComparisonCardProps {
  title: string;
  subtitle: string;
  metrics: MetricRow[];
  carrierInfo?: CarrierInfo;
  className?: string;
  isSelected?: boolean;
  selectLabel?: string;
  selectedLabel?: string;
  onToggleSelect?: () => void;
}

function ComparisonCard({
  title,
  subtitle,
  metrics,
  carrierInfo,
  className,
  isSelected = false,
  selectLabel = 'Select',
  selectedLabel = 'Selected',
  onToggleSelect,
}: ComparisonCardProps) {
  return (
    <Card
      className={cn(
        'relative p-6 transition-all',
        isSelected && 'border-primary border-2',
        className,
      )}
    >
      <div className="flex h-full flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-xl font-bold text-gray-950">{title}</h3>
            <p className="text-muted-foreground text-sm">{subtitle}</p>
          </div>
          {carrierInfo && (
            <div className="hidden shrink-0 sm:block">
              <CarrierBadge carrierInfo={carrierInfo} />
            </div>
          )}
        </div>

        {carrierInfo && (
          <div className="sm:hidden">
            <CarrierBadge carrierInfo={carrierInfo} />
          </div>
        )}

        {metrics.map((metric, index) => (
          <div
            key={index}
            className="mt-auto"
          >
            {index > 0 && <Separator className="mb-4" />}
            <p className="text-muted-foreground mb-2 text-balance text-sm">{metric.label}</p>
            <p
              className={cn('font-sans text-2xl font-bold text-gray-950', {
                'text-orange-500': metric.variant === 'warning',
              })}
            >
              <LtcCostDisplay value={metric.value} />
            </p>
            {metric.description && (
              <p className="text-muted-foreground mt-1 text-balance text-sm">
                {metric.description}
              </p>
            )}
          </div>
        ))}

        {onToggleSelect && (
          <Button
            variant={isSelected ? 'default' : 'outline'}
            className={cn(
              'w-full',
              !isSelected && 'border-primary text-primary hover:bg-primary/10',
            )}
            onClick={onToggleSelect}
          >
            {isSelected ? (
              <>
                <Check className="mr-2 size-4" />
                {selectedLabel}
              </>
            ) : (
              selectLabel
            )}
          </Button>
        )}
      </div>
    </Card>
  );
}

interface SummaryCardProps {
  netBenefit: number;
  isCcrcTypeA: boolean;
  productName: string;
}

function SummaryCard({ netBenefit, isCcrcTypeA, productName }: SummaryCardProps) {
  if (netBenefit === 0) {
    return null;
  }

  const planIsBetter = netBenefit > 0;

  let heading: string;
  let description: string;

  if (isCcrcTypeA) {
    heading = planIsBetter
      ? `With ${productName} vs Self-Funding Alone`
      : `Self-Funding Alone vs With ${productName}`;
    description = planIsBetter
      ? `more projected assets remaining when using ${productName} for the modeled scenario`
      : `more projected assets remaining without ${productName}`;
  } else {
    heading = planIsBetter ? 'With Insurance vs Self-Funding' : 'Self-Funding vs With Insurance';
    description = planIsBetter
      ? 'more projected assets remaining with the insurance plan'
      : 'more projected assets remaining without insurance';
  }

  return (
    <Card className="bg-primary/5 border-primary/40 p-6">
      <CardContent className="flex flex-col items-center p-0 text-center">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-600">{heading}</p>
        <div className="text-primary mb-1 text-3xl font-bold">
          +<LtcCostDisplay value={Math.abs(netBenefit)} />
        </div>
        <p className="text-sm text-gray-600">{description}</p>
      </CardContent>
    </Card>
  );
}

interface FundingBreakdownCardProps {
  allocations: Array<{
    selfFundingSourceId: string;
    accountName: string;
    amount: number;
    existingValue?: number;
  }>;
  totalPolicyCost: number;
  fundingSourcesSnapshot?: MultipleFundingSources;
}

function getExistingValueFromSnapshot(
  sourceId: string,
  snapshot?: MultipleFundingSources,
): number | null {
  const source = snapshot?.selfFunding?.[sourceId];
  if (source && typeof source === 'object' && 'existingAssetsValue' in source) {
    return source.existingAssetsValue ?? null;
  }
  return null;
}

function FundingBreakdownCard({
  allocations,
  totalPolicyCost,
  fundingSourcesSnapshot,
}: FundingBreakdownCardProps) {
  if (allocations.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border px-4">
      <Accordion
        type="single"
        collapsible
        className="w-full"
      >
        <AccordionItem
          value="funding-breakdown"
          className="border-none bg-transparent hover:bg-transparent data-[state=open]:bg-transparent"
        >
          <AccordionTrigger className="text-lg font-semibold text-gray-900 hover:no-underline">
            How your premium is funded
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-3">
              {allocations.map(allocation => {
                const existingValue =
                  allocation.existingValue ??
                  getExistingValueFromSnapshot(
                    allocation.selfFundingSourceId,
                    fundingSourcesSnapshot,
                  );

                return (
                  <div
                    key={allocation.selfFundingSourceId}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">{allocation.accountName}</p>
                      {existingValue != null && existingValue > 0 && (
                        <Badge
                          variant="secondary"
                          className="font-medium"
                        >
                          {formatCurrency(existingValue)}
                        </Badge>
                      )}
                    </div>
                    <span className="font-sans font-semibold text-red-600">
                      −{formatCurrency(allocation.amount)}
                    </span>
                  </div>
                );
              })}

              {totalPolicyCost > 0 && (
                <>
                  <Separator className="my-4" />
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-gray-900">Total Policy Cost</p>
                    <p className="font-sans font-semibold text-gray-900">
                      {formatCurrency(totalPolicyCost)}
                    </p>
                  </div>
                </>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

function getEndOfCareLabel(clientFirstName: string, partnerFirstName?: string): string {
  if (partnerFirstName) {
    return `Projected Assets at End of ${possessive(clientFirstName)} and ${possessive(partnerFirstName)} Modeled Long-Term Care`;
  }
  return `Projected Assets at End of ${possessive(clientFirstName)} Modeled Long-Term Care`;
}

interface PlanComparisonContentProps {
  planComparison: ReturnType<typeof usePlanComparisonScenarios>;
  selectedPlan: PlanSelection | null;
  onTogglePlan: (plan: PlanSelection) => void;
  isReadOnly?: boolean;
  fundingSourcesSnapshot?: MultipleFundingSources;
  hasExistingLtcPolicy?: boolean;
  clientFirstName: string;
  partnerFirstName?: string;
}

function PlanComparisonContent({
  planComparison,
  selectedPlan,
  onTogglePlan,
  isReadOnly = false,
  fundingSourcesSnapshot,
  hasExistingLtcPolicy = false,
  clientFirstName,
  partnerFirstName,
}: PlanComparisonContentProps) {
  const { flags } = useFeatureFlags();
  const { selectedQuote } = useAddPolicyFromQuote();
  // The program's own copy, so this card cannot name the wrong partner. Tooltip then body
  // reproduces the wording Edward Jones already had, with the right name for each program.
  const pilotProgram = useActivePilotProgram();
  const quoteApplyBlocked = pilotProgram?.blockedPages?.['quote-apply'];
  const careHasStarted = useCareHasStarted();
  const {
    withoutInsurance,
    withInsurance,
    selectedPolicy,
    fundingAllocations,
    netBenefit,
    benefitScenario,
    projectedLtcCost,
  } = planComparison;

  const totalPolicyCost = selectedPolicy?.totalCost ?? 0;
  const endOfCareLabel = getEndOfCareLabel(clientFirstName, partnerFirstName);

  // CCRC Type A products replace all "insurance" wording with the product name.
  const isCcrcTypeA = Boolean(selectedQuote?.ccrcTypeAProduct);
  const planProductName = selectedPolicy?.productName ?? 'CCRC Plan';

  let withoutCardTitle = 'Without Insurance';
  if (isCcrcTypeA) {
    withoutCardTitle = `Without ${planProductName}`;
  } else if (hasExistingLtcPolicy) {
    withoutCardTitle = 'Current Coverage';
  }

  const withoutCardSubtitle =
    hasExistingLtcPolicy && !isCcrcTypeA ? 'Self-Funding + existing policy' : 'Self-Funding only';

  const withoutCardSelectLabel =
    hasExistingLtcPolicy && !isCcrcTypeA
      ? 'Continue with self-funding & existing policy'
      : 'Continue with self-funding';

  const withCardTitle = isCcrcTypeA ? `With ${planProductName}` : 'With Insurance';

  let withCardSubtitle: string;

  if (isCcrcTypeA && planComparison.isCoupleMode && partnerFirstName) {
    withCardSubtitle = `Covers ${clientFirstName} & ${partnerFirstName}`;
  } else if (isCcrcTypeA) {
    withCardSubtitle = `Self-Funding + ${planProductName}`;
  } else if (flags.nycLifeDemo) {
    withCardSubtitle = 'Selected Policy';
  } else {
    withCardSubtitle = selectedPolicy?.productName || 'No Selected Policy';
  }

  const withCardSelectLabel = isCcrcTypeA
    ? `Continue with ${planProductName}`
    : 'Continue with insurance';

  const withCurrentAssetsLabel = isCcrcTypeA
    ? `Current Assets and Projected Coverage after ${planProductName} purchase.`
    : 'Current Assets and Max Coverage after Policy Purchase';

  const withStartOfCareLabel = isCcrcTypeA
    ? 'Projected Assets and Projected Coverage at Start of Long-Term Care.'
    : 'Projected Assets and Max Coverage at Start of Long-Term Care';

  const carrierName = flags.nycLifeDemo ? 'New York Life' : (selectedPolicy?.carrierName ?? '');
  const carrierInfo: CarrierInfo | undefined = selectedPolicy
    ? {
        carrierName: flags.nycLifeDemo ? 'New York Life' : selectedPolicy.carrierName,
        productName: flags.nycLifeDemo ? 'Selected Policy' : selectedPolicy.productName,
        logoSrc: getCarrierLogoSrc(carrierName),
        creditRating: AM_BEST_CARRIER_RATINGS[carrierName],
        quote: selectedQuote?.quote,
        isCcrcTypeA: Boolean(selectedQuote?.ccrcTypeAProduct),
      }
    : undefined;

  const gapDescription = (totalCoverage: number) =>
    `Your estimated LTC cost is ${formatCurrency(projectedLtcCost)}, but your total coverage is ${formatCurrency(totalCoverage)}.`;

  const withoutInsuranceMetrics: MetricRow[] = [
    { label: 'Current Assets', value: withoutInsurance.currentAssets },
    {
      label: 'Projected Assets at Start of Long-Term Care',
      value: withoutInsurance.projectedAssetsAtStart,
    },
    { label: endOfCareLabel, value: withoutInsurance.projectedAssetsAtEnd },
    ...(withoutInsurance.coverageGap > 0
      ? [
          {
            label: 'Estimated Unfunded LTC Cost',
            value: withoutInsurance.coverageGap,
            variant: 'warning' as const,
            description: gapDescription(withoutInsurance.totalCoverage),
          },
        ]
      : []),
  ];

  const withInsuranceMetrics: MetricRow[] = [
    {
      label: withCurrentAssetsLabel,
      value: withInsurance.currentAssets,
    },
    {
      label: withStartOfCareLabel,
      value: withInsurance.projectedAssetsAtStart,
    },
    { label: endOfCareLabel, value: withInsurance.projectedAssetsAtEnd },
    ...(withInsurance.coverageGap > 0
      ? [
          {
            label: 'Estimated Unfunded LTC Cost',
            value: withInsurance.coverageGap,
            variant: 'warning' as const,
            description: gapDescription(withInsurance.totalCoverage),
          },
        ]
      : []),
  ];

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <SummaryCard
        netBenefit={netBenefit}
        isCcrcTypeA={isCcrcTypeA}
        productName={planProductName}
      />

      <div className="grid gap-6 md:grid-cols-2">
        <ComparisonCard
          title={withoutCardTitle}
          subtitle={withoutCardSubtitle}
          metrics={withoutInsuranceMetrics}
          isSelected={selectedPlan === 'without-insurance'}
          selectLabel={withoutCardSelectLabel}
          selectedLabel="Selected"
          onToggleSelect={isReadOnly ? undefined : () => onTogglePlan('without-insurance')}
        />
        {quoteApplyBlocked ? (
          <Card className="flex items-center justify-center p-6">
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-950">With Insurance</h3>
              <p className="text-muted-foreground mt-2 text-balance text-sm">
                {`${pilotProgram?.restrictedTooltip ?? ''} ${quoteApplyBlocked.body}`.trim()}
              </p>
            </div>
          </Card>
        ) : careHasStarted ? (
          <Card className="flex items-center justify-center p-6">
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-950">With Insurance</h3>
              <p className="text-muted-foreground mt-2 text-balance text-sm">
                Long-term care has already started, so a new policy isn&apos;t applicable. Continue
                with self-funding to see your projection.
              </p>
            </div>
          </Card>
        ) : !selectedPolicy ? (
          <Card className="flex items-center justify-center p-6">
            <div className="flex flex-col items-center gap-4 text-center">
              <div>
                <h3 className="text-xl font-bold text-gray-950">With Insurance</h3>
                <p className="text-muted-foreground mt-2 text-balance text-sm">
                  Choose a policy from Insurance Options to see how it compares to self-funding.
                </p>
              </div>
              <LicensedAgentCta
                size="default"
                buttonClassName="w-xs sm:w-xs h-max sm:items-start sm:text-wrap py-3 sm:text-start sm:rounded-md [&_span]:text-wrap!"
              />
            </div>
          </Card>
        ) : (
          <ComparisonCard
            title={withCardTitle}
            subtitle={withCardSubtitle}
            metrics={withInsuranceMetrics}
            carrierInfo={carrierInfo}
            isSelected={selectedPlan === 'with-insurance'}
            selectLabel={withCardSelectLabel}
            selectedLabel="Selected"
            onToggleSelect={isReadOnly ? undefined : () => onTogglePlan('with-insurance')}
          />
        )}
      </div>

      <FundingBreakdownCard
        allocations={fundingAllocations}
        totalPolicyCost={totalPolicyCost}
        fundingSourcesSnapshot={fundingSourcesSnapshot}
      />
    </div>
  );
}

export function PlanComparisonStep() {
  const navigate = useNavigate();
  const client = useSelector(selectClient);
  const { flags } = useFeatureFlags();
  const { sessionType } = useSelector(selectSession);
  const featureFlags = useSelector(selectFeatureFlags);
  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;
  const skipPolicySteps = useSkipPolicySteps();
  const { goToStep } = useWizard();
  const { session, isLoading: isSessionLoading } = useSession();
  const flatStepConfigs = useGuidedPlanningFlatStepConfigs();
  const { isSaving, abandonSession, updateStepData } = useSessionMutations();
  const { finalizeSession, isFinalizing } = useFinalizeSession();
  const { validate, errors: validationErrors } = useFinalizeValidation();
  const planComparison = usePlanComparisonScenarios();

  const selectedPlan = useMemo((): PlanSelection | null => {
    // Don't apply defaults until session is loaded
    if (isSessionLoading) {
      return null;
    }

    // EDJ pilots and on-claim clients can only use without-insurance
    if (skipPolicySteps) {
      return 'without-insurance';
    }

    const saved = session?.stepData?.selectedPlanType;
    if (saved === 'with-insurance' || saved === 'without-insurance') {
      return saved;
    }
    // Only default if selectedPlanType was never set (undefined)
    // If it's explicitly null (user deselected), respect that
    if (saved === null) {
      return null;
    }
    // Default to whichever option has better projected assets
    return planComparison.netBenefit >= 0 ? 'with-insurance' : 'without-insurance';
  }, [
    session?.stepData?.selectedPlanType,
    isSessionLoading,
    planComparison.netBenefit,
    skipPolicySteps,
  ]);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showWithoutInsuranceDialog, setShowWithoutInsuranceDialog] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isAbandoning, setIsAbandoning] = useState(false);

  const isSessionComplete = session?.status === 'completed';

  const handleAdjustFunding = useCallback(() => {
    if (skipPolicySteps) {
      goToStep(getStepIndex('assessment-projection', flatStepConfigs));
    } else if (!planComparison.hasPolicy) {
      goToStep(getStepIndex('quotes-selection', flatStepConfigs));
    } else {
      goToStep(getStepIndex('quotes-funding', flatStepConfigs));
    }
  }, [goToStep, flatStepConfigs, skipPolicySteps, planComparison.hasPolicy]);

  const handleTogglePlan = useCallback(
    (plan: PlanSelection) => {
      const newSelection = selectedPlan === plan ? null : plan;
      setShowValidationErrors(false);
      updateStepData({ selectedPlanType: newSelection });
    },
    [selectedPlan, updateStepData],
  );

  const exitDestination = isD2C ? 'funding-coverage' : 'funding-sources';

  const handleFinalize = useCallback(async () => {
    await finalizeSession(planComparison);
    if (client.clientId) {
      navigate(`/clients/${client.clientId}/onboarding/${exitDestination}`);
    }
  }, [finalizeSession, planComparison, navigate, client.clientId, exitDestination]);

  const handleAbandonSession = useCallback(async () => {
    if (!client.clientId) return;

    setIsAbandoning(true);
    try {
      await abandonSession();
      navigate(`/clients/${client.clientId}/onboarding/${exitDestination}`);
    } finally {
      setIsAbandoning(false);
    }
  }, [abandonSession, client.clientId, navigate, exitDestination]);

  const handleNextClick = useCallback(() => {
    if (isSessionComplete) {
      if (client.clientId) {
        navigate(`/clients/${client.clientId}/onboarding/${exitDestination}`);
      }
      return;
    }

    if (!selectedPlan) {
      return;
    }

    if (selectedPlan === 'without-insurance') {
      setShowWithoutInsuranceDialog(true);
      return;
    }

    const result = validate();
    if (!result.isValid) {
      setShowValidationErrors(true);
      return;
    }

    setShowValidationErrors(false);
    setShowConfirmDialog(true);
  }, [isSessionComplete, client.clientId, navigate, selectedPlan, validate, exitDestination]);

  const handleConfirmFinalize = useCallback(async () => {
    setShowConfirmDialog(false);
    await handleFinalize();
  }, [handleFinalize]);

  const handleConfirmWithoutInsurance = useCallback(async () => {
    setShowWithoutInsuranceDialog(false);
    await handleAbandonSession();
  }, [handleAbandonSession]);

  const isWithoutInsurance = selectedPlan === 'without-insurance';
  const nextButtonLabel = isSessionComplete
    ? 'Done'
    : isWithoutInsurance
      ? 'Continue Without Insurance'
      : 'Finalize My Plan';
  const nextButtonIcon =
    isSessionComplete || isWithoutInsurance ? undefined : <Check className="size-5" />;
  const nextButtonVariant = isWithoutInsurance ? 'ghost' : 'default';
  const isNextDisabled = !isSessionComplete && !selectedPlan;

  return (
    <>
      <StepLayout
        title="Your Plan Comparison"
        subtitle="We've projected how these two strategies grow and pay for your modeled LTC needs."
        backButtonLabel={
          skipPolicySteps
            ? 'Back'
            : !planComparison.hasPolicy
              ? 'Policy Selection'
              : 'Adjust Funding'
        }
        nextButtonLabel={nextButtonLabel}
        nextButtonIcon={nextButtonIcon}
        nextButtonVariant={nextButtonVariant}
        onBack={handleAdjustFunding}
        onNext={handleNextClick}
        isLoading={isSaving || isFinalizing || isAbandoning}
        isNextDisabled={isNextDisabled}
      >
        <PlanComparisonContent
          planComparison={planComparison}
          selectedPlan={selectedPlan}
          onTogglePlan={handleTogglePlan}
          isReadOnly={isSessionComplete}
          fundingSourcesSnapshot={session?.stepData?.multipleFundingSourcesSnapshot}
          hasExistingLtcPolicy={
            flags.existingLtcPolicyTypeOnCard &&
            (session?.stepData?.selectedAssets ?? []).includes('existing-ltc-policy')
          }
          clientFirstName={client.clientFirstName || 'Client'}
          partnerFirstName={
            // In couple-mode (joint policy + linked partner), use the partner
            // client's first name directly so the label is correct even when
            // the intake survey didn't populate `partnerFirstName`. Otherwise
            // fall back to the intake-survey-driven label for non-joint
            // single-life policies on partnered households.
            planComparison.partnerFirstName ??
            (client.intakeSurvey?.householdPartnered === 'Yes'
              ? client.intakeSurvey?.partnerFirstName || undefined
              : undefined)
          }
        />
        {showValidationErrors && validationErrors.length > 0 && (
          <Alert
            variant="destructive"
            className="mx-auto mt-4 max-w-4xl"
          >
            <AlertDescription>
              <p className="text-sm font-medium ">
                Could not finalize your plan. Please review the following errors and try again:
              </p>
              <ul className="list-disc pl-4">
                {validationErrors.map(error => (
                  <li key={error.code}>{error.message}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </StepLayout>

      <AlertDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ready to finalize?</AlertDialogTitle>
            <AlertDialogDescription>
              This will save a snapshot of your current plan. It will not be editable after
              finalization.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmFinalize}>Finalize</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showWithoutInsuranceDialog}
        onOpenChange={setShowWithoutInsuranceDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Continue without insurance?</AlertDialogTitle>
            <AlertDialogDescription>
              You've chosen to rely on your existing savings and assets to cover future long-term
              care costs. You can always explore insurance options later if you change your mind.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go Back</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmWithoutInsurance}>
              Continue Without Insurance
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
